import { useLazyQuery } from "@apollo/client";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  startOfMonth,
} from "date-fns";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Page from "../../components/Page/Page";
import StatCard from "../../components/StatCard/StatCard";
import { SignupFlowResult } from "../../graphql/graphql";
import { SIGNUP_FLOW } from "../../graphql/queries-gql";

const StepCard = styled.div<{ $isActive?: boolean }>`
  width: 260px;
  padding: 16px 18px;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  background-color: ${(props) =>
    props.$isActive ? props.theme.cardBg : props.theme.surfaceHover};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${(props) => props.theme.borderStrong};
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: flex-start;
`;

const StepArrow = styled.div<{ $faded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 32px;
  margin: 0 auto;
  color: ${(props) =>
    props.$faded ? props.theme.border : props.theme.mutedTextColor};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ProgressBar = styled.div<{ $progress: number; $thick?: boolean }>`
  width: 100%;
  height: ${(props) => (props.$thick ? 4 : 2)}px;
  background-color: ${(props) => props.theme.surfaceActive};
  border-radius: 2px;
  margin: 10px 0 0 0;
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background-color: ${props => props.theme.colors.green};
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 4px 0;
`;

const StatValue = styled.span<{ $secondary?: boolean }>`
  font-size: ${(props) => (props.$secondary ? 18 : 26)}px;
  font-weight: ${(props) => (props.$secondary ? 500 : 650)};
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$secondary ? props.theme.mutedTextColor : props.theme.textColor};
`;

const StatInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
`;

const StatChange = styled.span<{ $isPositive?: boolean }>`
  font-size: 12.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${props => props.$isPositive ? props.theme.colors.green : props.theme.colors.red};
`;

const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 360px));
  gap: 16px;
  justify-content: center;
  margin-bottom: 8px;
`;

const Funnel = styled.div`
  display: flex;
  flex-direction: column;
`;

enum DateRange {
  Today,
  Yesterday,
  ThisMonth,
  Last24Hours,
  Last3Days,
  Last7Days,
  Last30Days,
}

const renderLoadingSkeleton = (isSummary: boolean = false) => (
  isSummary ? (
    <Skeleton variant="rounded" height={132} />
  ) : (
    <Skeleton variant="rounded" width={260} height={112} />
  )
);

const SignupFlow = () => {
  const [fromDate, setFromDate] = useState<Date>(startOfDay(new Date()));
  const [platform, setPlatform] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Today);
  const [fetchSignupFlow, { data, loading, error }] = useLazyQuery<{
    signupFlow: SignupFlowResult;
  }>(SIGNUP_FLOW, {
    fetchPolicy: "network-only",
    variables: {
      args: {
        fromDate,
        toDate,
        platform,
      },
    },
    onCompleted: (data) => {
      console.log("fetchSignupFlow onCompleted", data);
    },
  });

  useEffect(() => {
    if (!data && !loading && !error) {
      fetchSignupFlow();
    }
  }, [data, loading, error, fetchSignupFlow]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    switch (range) {
      case DateRange.Today:
        setFromDate(startOfDay(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Yesterday:
        setFromDate(startOfDay(addDays(new Date(), -1)));
        setToDate(endOfDay(addDays(new Date(), -1)));
        break;
      case DateRange.ThisMonth:
        setFromDate(startOfMonth(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Last24Hours:
        setFromDate(addHours(new Date(), -24));
        setToDate(new Date());
        break;
      case DateRange.Last3Days:
        setFromDate(addDays(new Date(), -3));
        setToDate(new Date());
        break;
      case DateRange.Last7Days:
        setFromDate(addDays(new Date(), -7));
        setToDate(new Date());
        break;
      case DateRange.Last30Days:
        setFromDate(addDays(new Date(), -30));
        setToDate(new Date());
        break;
    }
  };

  const renderStep = (title: string, count: number, activeCount: number, total: number, isPurchased: boolean = false) => {
    const dropOffPercent = ((total - count) / total * 100).toFixed(2);
    const activePercent = count > 0 ? ((activeCount / count) * 100).toFixed(2) : '0.00';
    const purchasePercent = ((count / total) * 100).toFixed(2);
    
    return (
      <StepCard $isActive={activeCount > 0}>
        <StatLabel>{title}</StatLabel>
        <ValueContainer>
          <StatValue>{count}</StatValue>
          {!isPurchased && count > 0 && activeCount !== count && (
            <StatValue $secondary>{activeCount}</StatValue>
          )}
        </ValueContainer>
        <StatInfo>
          {!isPurchased && activeCount > 0 && activeCount !== count && (
            <StatChange $isPositive={true}>
              {activeCount} active
            </StatChange>
          )}
          {isPurchased ? (
            <StatChange $isPositive={true}>
              {purchasePercent}%
            </StatChange>
          ) : (
            <StatChange $isPositive={false}>
              {count} of {total} ({((count / total) * 100).toFixed(2)}%)
            </StatChange>
          )}
        </StatInfo>
        <ProgressBar $progress={isPurchased ? Number(purchasePercent) : (count / total * 100)} />
      </StepCard>
    );
  };

  const renderArrow = (faded: boolean = false) => (
    <StepArrow $faded={faded}>
      <ArrowDownIcon />
    </StepArrow>
  );

  const renderLoadingState = () => (
    <Funnel>
      <SummaryContainer>
        {renderLoadingSkeleton(true)}
        {renderLoadingSkeleton(true)}
      </SummaryContainer>

      {renderArrow(true)}

      <StepContainer>
        {renderLoadingSkeleton()}
        {renderLoadingSkeleton()}
      </StepContainer>

      {renderArrow(true)}

      {[...Array(3)].map((_, index) => (
        <React.Fragment key={index}>
          <StepContainer>
            {renderLoadingSkeleton()}
          </StepContainer>
          {renderArrow(true)}
        </React.Fragment>
      ))}

      <StepContainer>
        {renderLoadingSkeleton()}
      </StepContainer>
    </Funnel>
  );

  return (
    <Page
      title="Signup flow"
      maxWidth={1200}
      actions={
        <ToggleButtonGroup
          value={platform}
          exclusive
          onChange={(_, value) => setPlatform(value)}
          size="small"
        >
          <ToggleButton value="ios">iOS</ToggleButton>
          <ToggleButton value="android">Android</ToggleButton>
        </ToggleButtonGroup>
      }
      toolbar={
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, value) => value !== null && handleDateRangeChange(value)}
          size="small"
        >
          <ToggleButton value={DateRange.Today}>Today</ToggleButton>
          <ToggleButton value={DateRange.Yesterday}>Yesterday</ToggleButton>
          <ToggleButton value={DateRange.ThisMonth}>This month</ToggleButton>
          <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
          <ToggleButton value={DateRange.Last3Days}>3 days</ToggleButton>
          <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
          <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {error && <ErrorAlert error={`Error: ${error.message}`} />}

      {loading ? renderLoadingState() : data && (
        <Funnel>
          <SummaryContainer>
            <StatCard label="Total users" value={data.signupFlow.totalUsers}>
              <ProgressBar $progress={100} $thick />
            </StatCard>
            <StatCard label="Completed signups" value={data.signupFlow.stepCompleted}>
              <StatChange $isPositive={true}>
                {((data.signupFlow.stepCompleted / data.signupFlow.totalUsers) * 100).toFixed(1)}% completion
              </StatChange>
              <ProgressBar $progress={(data.signupFlow.stepCompleted / data.signupFlow.totalUsers) * 100} $thick />
            </StatCard>
          </SummaryContainer>

          {renderArrow()}

          <StepContainer>
            {renderStep('Onboarding step 1', data.signupFlow.stepOnboarding1, data.signupFlow.stepOnboarding1Active, data.signupFlow.totalUsers)}
          </StepContainer>

          {renderArrow()}

          <StepContainer>
            {renderStep('Onboarding step 2', data.signupFlow.stepOnboarding2, data.signupFlow.stepOnboarding2Active, data.signupFlow.totalUsers)}
          </StepContainer>

          {renderArrow()}

          <StepContainer>
            {renderStep('Onboarding step 3', data.signupFlow.stepOnboarding3, data.signupFlow.stepOnboarding3Active, data.signupFlow.totalUsers)}
          </StepContainer>

          {renderArrow()}

          <StepContainer>
            {renderStep('Paywall', data.signupFlow.stepOnboardingPaywall, data.signupFlow.stepOnboardingPaywallActive, data.signupFlow.totalUsers)}
            {renderStep('Premium purchased', data.signupFlow.premiumPurchased, data.signupFlow.premiumPurchased, data.signupFlow.totalUsers, true)}
          </StepContainer>

          {renderArrow()}

          <StepContainer>
            {renderStep('Completed', data.signupFlow.stepCompleted, data.signupFlow.stepCompleted, data.signupFlow.totalUsers)}
          </StepContainer>
        </Funnel>
      )}
    </Page>
  );
};

export default SignupFlow;
